import { useEffect, useState } from 'react';
import { GeoJSON } from 'react-leaflet';
import { FeatureCollection } from 'geojson';
import { useLayers } from '../../context/LayersContext';
import L, { CircleMarkerOptions } from 'leaflet';

type StationLayerKey =
  | 'Bases de estações fluviométricas'
  | 'Bases de estações pluviométricas';

interface StationLayerConfig {
  key: StationLayerKey;
  url: string;
  color: string;
}

const PROXY = 'https://thingproxy.freeboard.io/fetch/';

const STATION_LAYER_CONFIGS: StationLayerConfig[] = [
  {
    key: 'Bases de estações fluviométricas',
    url:
      PROXY +
      'https://portal1.snirh.gov.br/server/rest/services/dados_abertos/Estacao_Fluviometrica/MapServer/0/query?where=1=1&outFields=*&f=geojson',
    color: '#1f78b4',
  },
  {
    key: 'Bases de estações pluviométricas',
    url:
      PROXY +
      'https://portal1.snirh.gov.br/server/rest/services/dados_abertos/Estacao_Pluviometrica/MapServer/0/query?where=1=1&outFields=*&f=geojson',
    color: '#33a02c',
  },
];

// Bounding box de Pernambuco (lat,long)
const PE_SW = L.latLng(-9.5, -41.5);
const PE_NE = L.latLng(-7.0, -34.8);
const PE_BOUNDS = L.latLngBounds(PE_SW, PE_NE);

export function RenderEstacoesHidrometeorologicas() {
  const { layers } = useLayers();
  const [dataMap, setDataMap] = useState<
    Record<StationLayerKey, FeatureCollection | null>
  >({
    'Bases de estações fluviométricas': null,
    'Bases de estações pluviométricas': null,
  });

  useEffect(() => {
    STATION_LAYER_CONFIGS.forEach(({ key, url }) => {
      if (layers.includes(key)) {
        fetch(url, { mode: 'cors' })
          .then((res) => {
            if (!res.ok) {
              throw new Error(`HTTP ${res.status}`);
            }
            return res.json();
          })
          .then((geo: FeatureCollection) => {
            setDataMap((prev) => ({ ...prev, [key]: geo }));
          })
          .catch((err) => {
            console.error(`Falha ao carregar ${key}:`, err);
            setDataMap((prev) => ({ ...prev, [key]: null }));
          });
      } else if (dataMap[key] !== null) {
        setDataMap((prev) => ({ ...prev, [key]: null }));
      }
    });
  }, [layers]);

  return (
    <>
      {STATION_LAYER_CONFIGS.map(({ key, color }) => {
        const geo = dataMap[key];
        if (!layers.includes(key) || !geo) return null;

        return (
          <GeoJSON
            key={key}
            data={geo}
            // filtra apenas pontos dentro do bbox de Pernambuco
            filter={(feature: any) => {
              if (
                feature.geometry &&
                feature.geometry.type === 'Point' &&
                Array.isArray(feature.geometry.coordinates)
              ) {
                const [lon, lat] = feature.geometry.coordinates;
                return PE_BOUNDS.contains(L.latLng(lat, lon));
              }
              return false;
            }}
            pointToLayer={(_, latlng) => {
              const opts: CircleMarkerOptions = {
                radius: 6,
                color,
                fillColor: color,
                fillOpacity: 0.7,
                weight: 1,
              };
              return L.circleMarker(latlng, opts);
            }}
            onEachFeature={(feature: any, layer) => {
              if (feature.properties?.NOME) {
                layer.bindPopup(
                  `<strong>${feature.properties.NOME}</strong><br/>${
                    feature.properties.ESTADONOME || ''
                  }`
                );
              }
            }}
          />
        );
      })}
    </>
  );
}
